import { useState, useEffect } from 'react';
import Dh from '../components/Dh';
import { useNavigate } from 'react-router-dom';
import { Briefcase, TrendingUp, Search, ExternalLink, Building2, Zap, Check, Rocket, Wallet, Layers, Users, BookOpen } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { BRAND } from '@/brand';

const IS_CORP = BRAND === 'corporate';

type ServiceUser = {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  country?: string;
  businessSetupData?: {
    type?: 'incorporate' | 'operate';
    companyName?: string;
    entityType?: string;
    emirate?: string;
    industry?: string;
    planName?: string;
    total?: number;
    submittedAt?: string;
    hasExpedited?: boolean;
    hasUpgrade?: boolean;
  };
  capitalAssessment?: {
    stage?: string;
    raiseAmount?: string;
    fundingType?: string;
    fundraisingStage?: string;
    challenge?: string;
    completedAt?: string;
  };
  purchasedPackages?: Pkg[];
  purchasedPackage?: Pkg;
  _id?: string;
  orderStatus?: string;
};

type Pkg = { name: string; flags?: number[] | null; band?: string | null; purchasedAt?: string | null; price?: number | null };

// The 5 accounting services a package's `flags` array maps to (mirrors /my-services).
const SVC_NAMES = ['Bookkeeping', 'VAT Registration', 'VAT Filing', 'Corporate Tax Registration', 'Corporate Tax Filing'];

// Order-status pill styling (matches the workflow states on the client detail page).
const ORDER_STATUS_PILL: Record<string, { label: string; cls: string }> = {
  new:           { label: 'New',           cls: 'bg-slate-100 text-slate-600' },
  in_progress:   { label: 'In progress',   cls: 'bg-blue-100 text-blue-700' },
  awaiting_docs: { label: 'Awaiting docs', cls: 'bg-amber-100 text-amber-700' },
  delivered:     { label: 'Delivered',     cls: 'bg-green-100 text-green-700' },
  cancelled:     { label: 'Cancelled',     cls: 'bg-red-100 text-red-700' },
};
const statusPill = (s?: string) => ORDER_STATUS_PILL[s || 'new'] || ORDER_STATUS_PILL.new;

export default function AdminServices() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ServiceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'setup' | 'assessment'>('setup');
  // Which kind of purchase the ledger shows. The list mixes two very different
  // things — accounting service packages and business-setup plans — so this
  // narrows it to one at a time instead of making the reader scan for the badge.
  const [kind, setKind] = useState<'all' | 'package' | 'setup'>('all');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_BASE_URL}/auth/users/all`, { headers });
      if (res.ok) {
        const data = await res.json();
        const list = data.users || data || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      console.error('Error fetching users:', e);
    } finally {
      setLoading(false);
    }
  };

  // A real business-setup order (placed, not just started).
  const isOrder = (u: ServiceUser) => u.businessSetupData && (u.businessSetupData.submittedAt || u.businessSetupData.total != null || u.businessSetupData.planName);

  // Build one line PER PURCHASED SERVICE — the accounting packages the client bought
  // (what they see on /my-services), plus each business-setup order. This is the
  // client's actual purchase ledger.
  const idOf = (u: ServiceUser) => u.uid || u._id || '';
  const allLines = users.flatMap(u => {
    const pkgs = (u.purchasedPackages && u.purchasedPackages.length) ? u.purchasedPackages : (u.purchasedPackage ? [u.purchasedPackage] : []);
    const lines: { key: string; user: ServiceUser; kind: 'package' | 'setup'; pkg?: Pkg; date: string }[] =
      pkgs.map((p, i) => ({ key: `${idOf(u)}-pkg-${i}`, user: u, kind: 'package' as const, pkg: p, date: p.purchasedAt || '' }));
    if (isOrder(u)) lines.push({ key: `${idOf(u)}-setup`, user: u, kind: 'setup', date: u.businessSetupData!.submittedAt || '' });
    return lines;
  }).sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());

  const serviceLines = allLines
    .filter(l => kind === 'all' || l.kind === kind)
    .filter(l =>
      `${l.user.firstName} ${l.user.lastName} ${l.user.email} ${l.user.businessSetupData?.companyName || ''} ${l.pkg?.name || ''}`.toLowerCase().includes(search.toLowerCase())
    );

  // The consulting raise intake is stored as `raiseCapitalForm` (the old
  // `capitalAssessment` field is never written by the app).
  const assessmentUsers = users.filter(u => (u as any).raiseCapitalForm?.completedAt);
  const filteredAssessment = assessmentUsers.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const clientsWithServices = new Set(allLines.map(l => idOf(l.user))).size;
  const packageCount = allLines.filter(l => l.kind === 'package').length;
  const setupCount = allLines.filter(l => l.kind === 'setup').length;
  const estRevenue = allLines.reduce((s, l) => s + (l.kind === 'package' ? (l.pkg?.price || 0) : (l.user.businessSetupData?.total || 0)), 0);

  const statCards = IS_CORP
    ? [
        { label: 'Services sold', value: allLines.length, icon: <Layers className="w-5 h-5 text-[var(--brand-d)]" />, bg: '#FFFBEB' },
        { label: 'Active clients', value: clientsWithServices, icon: <Users className="w-5 h-5 text-[var(--brand-d)]" />, bg: '#FFFBEB' },
        { label: 'Est. revenue', value: <><Dh />{estRevenue.toLocaleString()}</>, icon: <TrendingUp className="w-5 h-5 text-[var(--brand-d)]" />, bg: '#FEF3C7' },
        { label: 'Setup orders', value: setupCount, icon: <Rocket className="w-5 h-5 text-[var(--brand-d)]" />, bg: '#FFFBEB' },
      ]
    : [
        { label: 'Services sold', value: allLines.length, icon: <Layers className="w-5 h-5 text-[var(--brand-d)]" />, bg: '#EFF6FF' },
        { label: 'Active clients', value: clientsWithServices, icon: <Users className="w-5 h-5 text-[var(--brand-d)]" />, bg: '#EFF6FF' },
        { label: 'Raise requests', value: assessmentUsers.length, icon: <TrendingUp className="w-5 h-5 text-green-600" />, bg: '#F0FDF4' },
      ];

  // The ledger's kind switcher. Counts are of the whole set, not the search
  // result, so the numbers don't jump while you type.
  const kindFilters = [
    { id: 'all' as const,     label: 'All',              count: allLines.length, icon: <Layers className="w-[15px] h-[15px]" /> },
    { id: 'package' as const, label: 'Service packages', count: packageCount,    icon: <BookOpen className="w-[15px] h-[15px]" /> },
    { id: 'setup' as const,   label: 'Setup plans',      count: setupCount,      icon: <Rocket className="w-[15px] h-[15px]" /> },
  ];

  const tabs = IS_CORP
    ? [{ id: 'setup' as const, label: 'Purchased services', count: allLines.length, icon: <Layers className="w-4 h-4" /> }]
    : [
        { id: 'setup' as const, label: 'Purchased services', count: allLines.length, icon: <Layers className="w-4 h-4" /> },
        { id: 'assessment' as const, label: 'Capital Assessments', count: assessmentUsers.length, icon: <TrendingUp className="w-4 h-4" /> },
      ];

  return (
    <AdminLayout
      section="Services"
      title="Services & Orders"
      icon={Briefcase}
      subtitle="Every client's active services, order status and payment state."
    >
      <div className="ax-toolrow">
        <div className="ax-search">
          <Search />
          <input
            type="text"
            placeholder="Search users…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {activeTab === 'setup' && (
          <div className="tabs" role="group" aria-label="Filter by purchase type">
            {kindFilters.map(f => (
              <button
                key={f.id}
                type="button"
                className={`tab${kind === f.id ? ' active' : ''}`}
                onClick={() => setKind(f.id)}
                aria-pressed={kind === f.id}
              >
                {f.icon}
                {f.label}
                <span className="cnt">{f.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-6">
        <p className={`text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--brand-d)]`}>{IS_CORP ? 'Accounting & Corporate' : 'Consulting'}</p>
        <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight mt-1">Orders</h1>
        <p className="text-sm text-slate-500 mt-1">Every service your clients have purchased.</p>
      </div>

      {/* Stats */}
      <div className={`grid gap-4 mb-6 grid-cols-2 ${statCards.length === 4 ? 'lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
        {statCards.map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center gap-4 transition hover:shadow-md hover:-translate-y-0.5">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: s.bg }}>
              {s.icon}
            </div>
            <div>
              <p className="text-3xl font-black text-slate-900 tabular-nums leading-none">{s.value}</p>
              <p className="text-sm text-slate-500 font-medium mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main table card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 flex">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-[var(--brand)] text-[var(--brand-d)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === tab.id ? 'bg-[var(--brand-soft)] text-[var(--brand-d)]' : 'bg-gray-100 text-gray-500'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--brand)] mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Loading data...</p>
          </div>
        ) : activeTab === 'setup' ? (
          serviceLines.length === 0 ? (
            <div className="p-16 text-center text-gray-400">
              <Layers className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="font-medium text-sm">
                {search
                  ? 'No results match your search'
                  : kind === 'package' ? 'No service packages purchased yet'
                  : kind === 'setup'   ? 'No setup plans ordered yet'
                  : 'No services purchased yet'}
              </p>
            </div>
          ) : (
            <div className="p-4 md:p-5 space-y-3">
              {serviceLines.map(line => {
                const user = line.user;
                const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || '?';
                const uid = user.uid || user._id;
                const viewCls = 'text-[var(--brand-d)] hover:underline';

                // Business-setup order line
                if (line.kind === 'setup') {
                  const bd = user.businessSetupData || {};
                  const isInc = bd.type === 'incorporate';
                  const st = statusPill(user.orderStatus);
                  return (
                    <div key={line.key} className="group flex flex-col lg:flex-row lg:items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 md:p-5 shadow-sm transition hover:shadow-md hover:border-slate-200">
                      <div className="flex items-center gap-3 lg:w-60 shrink-0">
                        <div className={`w-11 h-11 rounded-xl grid place-items-center text-white text-sm font-bold shrink-0 ${isInc ? 'bg-gradient-to-br from-[var(--brand)] to-[var(--brand-d)]' : 'bg-gradient-to-br from-orange-500 to-rose-500'}`}>{initials}</div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900 truncate">{user.firstName} {user.lastName}</p>
                          <p className="text-xs text-slate-400 truncate">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${isInc ? 'bg-[var(--brand-soft)] text-[var(--brand-d)]' : 'bg-rose-100 text-rose-700'}`}>
                            {isInc ? <Rocket className="w-3 h-3" /> : <Wallet className="w-3 h-3" />}Business Setup · {isInc ? 'Incorporate' : 'Operate'}
                          </span>
                          <span className="text-sm font-semibold text-slate-800">{bd.planName || 'Setup order'}</span>
                          {bd.emirate && <span className="text-xs text-slate-400">· {bd.emirate}</span>}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          {bd.companyName && <span className="text-xs font-medium text-slate-500">{bd.companyName}</span>}
                          {bd.hasExpedited && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--brand-soft)] text-[var(--brand-d)] text-[10px] font-bold rounded-full"><Zap className="w-3 h-3" /> Expedited</span>}
                          {bd.hasUpgrade && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-full"><Check className="w-3 h-3" /> Compliance</span>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between lg:justify-end gap-4 lg:gap-6 shrink-0">
                        <div className="text-right">
                          <p className="text-lg font-black text-slate-900 tabular-nums whitespace-nowrap">{bd.total != null ? <><Dh />{bd.total.toLocaleString()}</> : '-'}</p>
                          <p className="text-[11px] text-slate-400">{bd.submittedAt ? new Date(bd.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${st.cls}`}>{st.label}</span>
                        <button onClick={() => navigate(`/admin/user/${uid}`)} className={`inline-flex items-center gap-1 text-xs font-bold whitespace-nowrap ${viewCls}`}>View <ExternalLink className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  );
                }

                // Purchased accounting service package line
                const pkg = line.pkg!;
                const flags = Array.isArray(pkg.flags) ? pkg.flags : [];
                const included = SVC_NAMES.filter((_, i) => flags[i] === 1);
                return (
                  <div key={line.key} className="group flex flex-col lg:flex-row lg:items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 md:p-5 shadow-sm transition hover:shadow-md hover:border-slate-200">
                    <div className="flex items-center gap-3 lg:w-60 shrink-0">
                      <div className="w-11 h-11 rounded-xl grid place-items-center text-white text-sm font-bold shrink-0 bg-gradient-to-br from-[var(--brand)] to-[var(--brand-d)]">{initials}</div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{user.firstName} {user.lastName}</p>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[var(--brand-soft)] text-[var(--brand-d)]"><BookOpen className="w-3 h-3" /> Service Package</span>
                        <span className="text-sm font-semibold text-slate-800">{pkg.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                        {included.length > 0 ? included.map(name => (
                          <span key={name} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full">{name}</span>
                        )) : <span className="text-xs text-slate-400">Package services</span>}
                        {flags[0] === 1 && pkg.band && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--brand-soft)] text-[var(--brand-d)] text-[10px] font-bold rounded-full"><TrendingUp className="w-3 h-3" /> {pkg.band} txns/mo</span>}
                      </div>
                    </div>
                    <div className="flex items-center justify-between lg:justify-end gap-4 lg:gap-6 shrink-0">
                      <div className="text-right">
                        <p className="text-lg font-black text-slate-900 tabular-nums whitespace-nowrap">{pkg.price != null ? <><Dh />{pkg.price.toLocaleString()}</> : `${included.length} service${included.length !== 1 ? 's' : ''}`}</p>
                        <p className="text-[11px] text-slate-400">{pkg.purchasedAt ? new Date(pkg.purchasedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap bg-green-100 text-green-700">Active</span>
                      <button onClick={() => navigate(`/admin/user/${uid}`)} className={`inline-flex items-center gap-1 text-xs font-bold whitespace-nowrap ${viewCls}`}>View <ExternalLink className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          filteredAssessment.length === 0 ? (
            <div className="p-16 text-center text-gray-400">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="font-medium text-sm">{search ? 'No results match your search' : 'No capital assessments submitted yet'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['User', 'Round', 'Raise Amount', 'Capital Type', 'Company', 'Completed', ''].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-6 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAssessment.map(user => (
                    <tr key={user.uid} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--brand-soft)] text-[var(--brand-d)] capitalize whitespace-nowrap">
                          {String((user as any).raiseCapitalForm?.roundType || '').replace(/-/g, ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {(user as any).raiseCapitalForm?.raiseAmount || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 capitalize">{(user as any).raiseCapitalForm?.capitalType || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-[220px] truncate">{(user as any).raiseCapitalForm?.companyInfo || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {(user as any).raiseCapitalForm?.completedAt
                          ? new Date((user as any).raiseCapitalForm.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => navigate(`/admin/user/${user.uid || (user as any)._id}`)}
                          className="flex items-center gap-1 text-xs font-semibold text-[var(--brand-d)] hover:text-[var(--brand-d)] transition whitespace-nowrap"
                        >
                          View <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </AdminLayout>
  );
}
